/*
 * Установка приложения на устройство.
 *
 * Определяет, каким способом это вообще возможно на текущем устройстве,
 * придерживает событие установки и помнит отказ.
 */

import RELEASE from '../../../static/release.json';
import { currentBrowser, type BrowserName, type PlatformName } from '$utils/browser';
import { guideFor, type InstallGuide } from '$components/install/guides';

const DISMISSED_KEY = 'andresot:install-dismissed';
/** Версия, о которой человеку уже рассказали. */
const SEEN_VERSION_KEY = 'andresot:seen-version';

/** Какая версия сейчас запущена. Одна на всё приложение — из файла сборки. */
export const CURRENT_RELEASE: Release = RELEASE;

export interface Release {
	version: string;
	date: string;
	notes: string[];
}

/**
 * Событие установки есть только в браузерах на движке Chromium; в типах
 * стандартной библиотеки его нет, поэтому описываем сами.
 */
interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<void>;
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Что этот браузер умеет с установкой.
 *
 * Раньше здесь перечислялись способы («Safari на iOS», «Chromium»), а всё
 * остальное скопом считалось неумеющим — из-за чего Firefox на Android и
 * Safari на Mac, которые ставить умеют, оставались без единого слова.
 */
export type InstallCapability =
	/** Уже открыто как приложение — предлагать нечего. */
	| 'installed'
	/**
	 * Встроенное окно внутри чужого приложения. Там нет ни установки, ни
	 * служебных сценариев: единственный верный совет — открыть настоящий
	 * браузер.
	 */
	| 'in-app'
	/** Браузер прислал событие установки: хватает одной кнопки. */
	| 'prompt'
	/** Ставить умеет, но только через своё меню — показываем шаги. */
	| 'manual'
	/** Не умеет вовсе: честнее сказать об этом, чем выдумывать шаги. */
	| 'unsupported';

class InstallState {
	capability = $state<InstallCapability>('unsupported');
	/** Какой это браузер и какое устройство — по ним подбирается инструкция. */
	browser = $state<BrowserName>('unknown');
	platform = $state<PlatformName>('unknown');
	/** Нужны ли инструкции именно для системных настроек iPhone/iPad. */
	isIOS = $state(false);
	/** Открыт полноэкранный экран установки. */
	screenOpen = $state(false);
	/** Есть ли готовое обновление приложения. */
	updateReady = $state(false);
	/**
	 * Версия, которая лежит на сервере, если она новее запущенной.
	 *
	 * Спрашиваем сеть, а не только служебный сценарий: на iOS он замечает новую
	 * сборку не сразу, и приложение месяцами оставалось бы старым, ничего об
	 * этом не говоря.
	 */
	latest = $state<Release | null>(null);
	/** Скрыта ли карточка обновления до следующего запуска. */
	updateHidden = $state(false);
	/**
	 * Приложение обновилось само, и человек об этом ещё не знает.
	 *
	 * Обычный путь именно такой: служебный сценарий сначала идёт в сеть, и
	 * запуск с интернетом уже приносит свежую сборку. Сказать «доступно
	 * обновление» тут нечего — обновление уже стоит; сказать надо, что оно
	 * приехало.
	 */
	justUpdated = $state(false);

	private deferred: BeforeInstallPromptEvent | null = null;
	private waitingWorker: ServiceWorker | null = null;

	/** Установка возможна прямо отсюда. */
	canInstall = $derived(this.capability === 'prompt' || this.capability === 'manual');

	/**
	 * Отсюда установить нельзя, но объяснить почему — можно и нужно: человек
	 * иначе останется в браузерном окне без уведомлений и без понимания причин.
	 */
	needsAnotherBrowser = $derived(this.capability === 'in-app' || this.capability === 'unsupported');

	/** Шаги для того браузера, в котором человек сейчас. */
	guide = $derived<InstallGuide>(guideFor(this.browser, this.platform));

	init(): void {
		if (typeof window === 'undefined') return;

		const detected = currentBrowser();
		this.browser = detected.browser;
		this.platform = detected.platform;
		this.isIOS = detected.platform === 'ios';
		this.capability = detectCapability(this.browser, this.platform);

		window.addEventListener('beforeinstallprompt', (event) => {
			// Отменяем показ встроенной полоски: своё предложение мы покажем
			// в подходящий момент, а не поверх экрана входа.
			event.preventDefault();

			this.deferred = event as BeforeInstallPromptEvent;
			// Установленному приложению событие не приходит, но проверка дешевле
			// разбирательства, если однажды придёт.
			if (this.capability !== 'installed') this.capability = 'prompt';
		});

		window.addEventListener('appinstalled', () => {
			this.deferred = null;
			this.capability = 'installed';
			this.screenOpen = false;
		});

		/*
		 * Экран установки встречает только на телефоне и только если человек
		 * его ещё не закрывал: на каждом заходе это раздражало бы. На
		 * компьютере он не всплывает вовсе — там установка никуда не убежит и
		 * доступна из настроек, а закрывать переписку ради предложения незачем.
		 */
		const narrow = window.matchMedia('(max-width: 900px)').matches;
		if (narrow && this.capability !== 'installed' && !wasDismissed()) {
			this.screenOpen = true;
		}

		this.justUpdated = shouldAnnounceUpdate();

		void this.watchForUpdates();
		void this.checkVersion();

		// Вернулись на экран — самое время узнать, не вышло ли новое.
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') void this.checkVersion();
		});
	}

	/**
	 * Спросить у сервера, какая версия выложена.
	 *
	 * Без сохранения: сохранённый ответ означал бы старую версию, а это ровно
	 * то, о чём мы и пытаемся сообщить.
	 */
	async checkVersion(): Promise<Release | null> {
		try {
			const response = await fetch('/release.json', { cache: 'no-store' });
			if (!response.ok) return null;

			const release = (await response.json()) as Release;
			if (!release?.version || release.version === CURRENT_RELEASE.version) {
				this.latest = null;
				return null;
			}

			this.latest = release;
			return release;
		} catch {
			// Сети нет — молчим: сообщать об обновлении сейчас нечего.
			return null;
		}
	}

	/** Спрятать карточку обновления до следующего запуска. */
	hideUpdate(): void {
		this.updateHidden = true;
	}

	/**
	 * «Понятно»: про эту версию человеку рассказали, больше не напоминаем.
	 * Пока он не нажал, карточка вернётся при следующем запуске — пропущенное
	 * обновление иначе так и осталось бы незамеченным.
	 */
	acknowledgeUpdate(): void {
		this.justUpdated = false;
		rememberSeenVersion(CURRENT_RELEASE.version);
	}

	/** Показать встроенное окно установки. Работает только в Chromium. */
	async promptInstall(): Promise<boolean> {
		if (!this.deferred) return false;

		await this.deferred.prompt();
		const { outcome } = await this.deferred.userChoice;

		// Событие одноразовое: повторно вызвать то же нельзя.
		this.deferred = null;

		return outcome === 'accepted';
	}

	openScreen(): void {
		this.screenOpen = true;
	}

	closeScreen(remember = true): void {
		this.screenOpen = false;
		if (remember) rememberDismissal();
	}

	/**
	 * Применить обновление и перезагрузить приложение.
	 *
	 * Готовая сборка ждёт своей очереди — её достаточно пропустить вперёд. Если
	 * она ещё не скачана (сеть сказала о новой версии раньше служебного
	 * сценария), просим его сходить за ней и перезагружаемся сами: иначе
	 * нажатие «Обновить сейчас» осталось бы без ответа.
	 */
	async applyUpdate(): Promise<void> {
		if (this.waitingWorker) {
			this.waitingWorker.postMessage('skip-waiting');

			// Перезагружаем после того, как новая версия возьмёт управление,
			// иначе страница обновится на старом коде.
			navigator.serviceWorker?.addEventListener('controllerchange', () => location.reload(), {
				once: true
			});
			return;
		}

		try {
			const registration = await navigator.serviceWorker?.getRegistration();
			await registration?.update();
		} catch {
			// Не вышло — перезагрузка всё равно принесёт свежие файлы.
		}
		location.reload();
	}

	/**
	 * Установленное приложение может месяцами держать старую версию: браузер
	 * не перезагружает его сам. Поэтому следим за появлением новой сборки.
	 */
	private async watchForUpdates(): Promise<void> {
		if (!('serviceWorker' in navigator)) return;

		try {
			const registration = await navigator.serviceWorker.ready;

			if (registration.waiting) {
				this.waitingWorker = registration.waiting;
				this.updateReady = true;
			}

			registration.addEventListener('updatefound', () => {
				const installing = registration.installing;
				if (!installing) return;

				installing.addEventListener('statechange', () => {
					// `controller` пуст при самой первой установке — тогда это
					// не обновление, а первый запуск, и звать перезагружаться незачем.
					if (installing.state === 'installed' && navigator.serviceWorker.controller) {
						this.waitingWorker = installing;
						this.updateReady = true;
					}
				});
			});
		} catch {
			// Служебный сценарий не зарегистрирован — обновления просто
			// приходят обычной перезагрузкой.
		}
	}
}

function detectCapability(browser: BrowserName, platform: PlatformName): InstallCapability {
	const standalone =
		window.matchMedia('(display-mode: standalone)').matches ||
		// Своё свойство Safari: стандартный признак там не работает.
		(navigator as Navigator & { standalone?: boolean }).standalone === true;

	if (standalone) return 'installed';
	if (browser === 'in-app') return 'in-app';

	/*
	 * Событие установки приходит позже — до него судим по инструкции: если
	 * для этого браузера есть работающие шаги, приложение можно поставить
	 * своими руками. Chromium вскоре пришлёт событие и заменит шаги кнопкой.
	 */
	return guideFor(browser, platform).unsupported ? 'unsupported' : 'manual';
}

function wasDismissed(): boolean {
	try {
		return localStorage.getItem(DISMISSED_KEY) === '1';
	} catch {
		return false;
	}
}

/**
 * Стоит ли рассказывать о случившемся обновлении.
 *
 * Первый в жизни запуск здороваться обновлением не должен: рассказывать не о
 * чем. Отличаем его по служебному сценарию — если он уже управляет страницей,
 * приложение здесь не впервые. Иначе версия запоминается молча.
 */
function shouldAnnounceUpdate(): boolean {
	let seen: string | null = null;
	try {
		seen = localStorage.getItem(SEEN_VERSION_KEY);
	} catch {
		// Приватный режим: помнить негде — и напоминать не о чем.
		return false;
	}

	if (seen === CURRENT_RELEASE.version) return false;

	if (seen === null && !navigator.serviceWorker?.controller) {
		rememberSeenVersion(CURRENT_RELEASE.version);
		return false;
	}

	return true;
}

function rememberSeenVersion(version: string): void {
	try {
		localStorage.setItem(SEEN_VERSION_KEY, version);
	} catch {
		// см. выше
	}
}

function rememberDismissal(): void {
	try {
		localStorage.setItem(DISMISSED_KEY, '1');
	} catch {
		// Приватный режим — предложение появится снова, ничего страшного.
	}
}

export const install = new InstallState();
